import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { 
  Loader2, ArrowLeft, Send, User, Mail, Clock, CheckCircle2, 
  HelpCircle, Inbox, XCircle, RefreshCw, Paperclip, Image, FileText, X
} from 'lucide-react';

var STATUS_CONFIG = {
  'open': { className: 'bg-blue-500/20 text-blue-600 dark:text-blue-400', icon: Inbox, label: 'Offen' },
  'in_progress': { className: 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400', icon: Clock, label: 'In Bearbeitung' },
  'waiting_customer': { className: 'bg-purple-500/20 text-purple-600 dark:text-purple-400', icon: HelpCircle, label: 'Wartet auf Antwort' },
  'resolved': { className: 'bg-green-500/20 text-green-600 dark:text-green-400', icon: CheckCircle2, label: 'Gelöst' },
  'closed': { className: 'bg-slate-500/20 text-slate-600 dark:text-slate-400', icon: XCircle, label: 'Geschlossen' }
};

export var PortalSupportDetail = function() {
  var params = useParams();
  var ticketId = params.ticketId;
  var auth = useAuth();
  var token = auth.token;
  
  var ticketState = useState(null);
  var ticket = ticketState[0];
  var setTicket = ticketState[1];
  
  var messagesState = useState([]);
  var messages = messagesState[0];
  var setMessages = messagesState[1];
  
  var attachmentsState = useState({});
  var attachments = attachmentsState[0];
  var setAttachments = attachmentsState[1];
  
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var sendingState = useState(false);
  var sending = sendingState[0];
  var setSending = sendingState[1];
  
  var replyState = useState('');
  var replyText = replyState[0];
  var setReplyText = replyState[1];
  
  var previewState = useState({ open: false, url: '', type: '', filename: '' });
  var preview = previewState[0];
  var setPreview = previewState[1];
  
  var uploadingState = useState(false);
  var uploading = uploadingState[0];
  var setUploading = uploadingState[1];
  
  var pendingAttachmentsState = useState([]);
  var pendingAttachments = pendingAttachmentsState[0];
  var setPendingAttachments = pendingAttachmentsState[1];
  
  var fileInputRef = useRef(null);
  var backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  var fetchTicket = function() {
    setLoading(true);
    fetch(backendUrl + '/api/portal/tickets/' + ticketId, {
      headers: { 'Authorization': 'Bearer ' + token }
    }).then(function(response) {
      if (response.ok) {
        return response.json().then(function(data) {
          setTicket(data.ticket);
          setMessages(data.messages || []);
          var allAttIds = [];
          (data.messages || []).forEach(function(m) {
            if (m.attachments) {
              allAttIds = allAttIds.concat(m.attachments);
            }
          });
          if (allAttIds.length > 0) {
            Promise.all(allAttIds.map(function(attId) {
              return fetch(backendUrl + '/api/attachments/' + attId + '/info')
                .then(function(r) { return r.ok ? r.json() : null; })
                .catch(function() { return null; });
            })).then(function(results) {
              var attMap = {};
              results.forEach(function(att) {
                if (att) attMap[att.id] = att;
              });
              setAttachments(attMap);
            });
          }
        });
      } else {
        toast.error('Ticket nicht gefunden');
      }
    }).catch(function() {
      toast.error('Fehler beim Laden');
    }).finally(function() {
      setLoading(false);
    });
  };

  useEffect(function() {
    fetchTicket();
  }, [ticketId]);

  var handleFileSelect = function(e) {
    var files = Array.from(e.target.files || []);
    var allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf'];
    
    files.forEach(function(file) {
      if (!allowedTypes.includes(file.type)) {
        toast.error('Nur Bilder und PDFs erlaubt: ' + file.name);
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Datei zu groß (max 10MB): ' + file.name);
        return;
      }
      
      setUploading(true);
      var formData = new FormData();
      formData.append('file', file);
      
      fetch(backendUrl + '/api/portal/tickets/' + ticketId + '/attachments', {
        method: 'POST',
        headers: { 'Authorization': 'Bearer ' + token },
        body: formData
      }).then(function(response) {
        if (response.ok) {
          return response.json().then(function(data) {
            setPendingAttachments(function(prev) {
              return prev.concat([{
                id: data.attachment_id,
                filename: data.filename,
                size: data.size,
                content_type: data.content_type
              }]);
            });
            toast.success('Anhang hochgeladen');
          });
        } else {
          return response.json().then(function(data) {
            toast.error(data.detail || 'Upload fehlgeschlagen');
          });
        }
      }).catch(function() {
        toast.error('Upload fehlgeschlagen');
      }).finally(function() {
        setUploading(false);
      });
    });
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  var handleSendReply = function() {
    if (!replyText.trim()) {
      toast.error('Bitte gib eine Nachricht ein');
      return;
    }

    setSending(true);
    fetch(backendUrl + '/api/portal/tickets/' + ticketId + '/reply', {
      method: 'POST',
      headers: {
        'Authorization': 'Bearer ' + token,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ 
        message: replyText,
        attachment_ids: pendingAttachments.map(function(a) { return a.id; })
      })
    }).then(function(response) {
      if (response.ok) {
        toast.success('Antwort gesendet');
        setReplyText('');
        setPendingAttachments([]);
        fetchTicket();
      } else {
        toast.error('Fehler beim Senden');
      }
    }).catch(function() {
      toast.error('Fehler beim Senden');
    }).finally(function() {
      setSending(false);
    });
  };

  var openAttachmentPreview = function(attachmentId) {
    var att = attachments[attachmentId];
    if (!att) return;
    
    var isImage = att.content_type.startsWith('image/');
    var isPdf = att.content_type === 'application/pdf';
    
    if (isImage || isPdf) {
      setPreview({
        open: true,
        url: backendUrl + '/api/attachments/' + attachmentId,
        type: isImage ? 'image' : 'pdf',
        filename: att.filename
      });
    }
  };

  var removePendingAttachment = function(id) {
    setPendingAttachments(function(prev) {
      return prev.filter(function(a) { return a.id !== id; });
    });
  };

  if (loading) {
    return React.createElement('div', { className: 'flex items-center justify-center py-20' },
      React.createElement(Loader2, { className: 'h-8 w-8 animate-spin text-primary' })
    );
  }

  if (!ticket) {
    return React.createElement('div', { className: 'text-center py-20' },
      React.createElement('p', { className: 'text-muted-foreground' }, 'Ticket nicht gefunden'),
      React.createElement(Button, { asChild: true, variant: 'link', className: 'text-primary mt-4' },
        React.createElement(Link, { to: '/portal/support' }, 'Zurück zur Übersicht')
      )
    );
  }

  var statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG['open'];
  var StatusIcon = statusConfig.icon;
  var isClosedOrResolved = ticket.status === 'closed' || ticket.status === 'resolved';

  var messageElements = messages.map(function(message, index) {
    var isInbound = message.direction === 'inbound';
    
    var attachmentElements = null;
    if (message.attachments && message.attachments.length > 0) {
      var attItems = message.attachments.map(function(attId) {
        var att = attachments[attId];
        if (!att) return null;
        
        var isImage = att.content_type.startsWith('image/');
        var AttIcon = isImage ? Image : FileText;
        
        return React.createElement('button', {
          key: attId,
          onClick: function() { openAttachmentPreview(attId); },
          className: 'flex items-center gap-2 bg-muted hover:bg-muted/80 px-3 py-2 rounded text-xs text-muted-foreground transition-colors cursor-pointer'
        },
          React.createElement(AttIcon, { className: 'h-4 w-4' }),
          React.createElement('span', { className: 'truncate max-w-32' }, att.filename),
          React.createElement('span', { className: 'text-muted-foreground/70' }, '(' + Math.round(att.size / 1024) + ' KB)')
        );
      }).filter(Boolean);
      
      if (attItems.length > 0) {
        attachmentElements = React.createElement('div', { className: 'mt-3 pt-3 border-t border-border' },
          React.createElement('p', { className: 'text-muted-foreground text-xs mb-2 flex items-center gap-1' },
            React.createElement(Paperclip, { className: 'h-3 w-3' }),
            'Klicken für Vorschau'
          ),
          React.createElement('div', { className: 'flex flex-wrap gap-2' }, attItems)
        );
      }
    }
    
    return React.createElement('div', {
      key: message.id,
      className: 'p-4 rounded-lg ' + (isInbound ? 'bg-muted/50 border border-border' : 'bg-primary/10 border border-primary/30'),
      'data-testid': 'portal-message-' + index
    },
      React.createElement('div', { className: 'flex items-start justify-between mb-3' },
        React.createElement('div', { className: 'flex items-center gap-2' },
          React.createElement('div', { className: 'w-8 h-8 rounded-full flex items-center justify-center ' + (isInbound ? 'bg-muted-foreground/20' : 'bg-primary') },
            isInbound ? React.createElement(User, { className: 'h-4 w-4 text-muted-foreground' }) : React.createElement(Mail, { className: 'h-4 w-4 text-primary-foreground' })
          ),
          React.createElement('div', null,
            React.createElement('p', { className: 'text-foreground font-medium text-sm' },
              isInbound ? 'Du' : (message.from_name || 'Wolkenhelden Support')
            ),
            React.createElement('p', { className: 'text-muted-foreground/70 text-xs' },
              isInbound ? message.from_email : 'Support-Team'
            )
          )
        ),
        React.createElement('p', { className: 'text-muted-foreground text-xs' }, new Date(message.created_at).toLocaleString('de-DE'))
      ),
      React.createElement('div', { className: 'text-foreground/80 whitespace-pre-wrap text-sm leading-relaxed' }, message.body_plain),
      attachmentElements
    );
  });

  var pendingAttachmentElements = pendingAttachments.length > 0 ? 
    React.createElement('div', { className: 'flex flex-wrap gap-2 mb-4' },
      pendingAttachments.map(function(att) {
        var isImage = att.content_type.startsWith('image/');
        var AttIcon = isImage ? Image : FileText;
        return React.createElement('div', {
          key: att.id,
          className: 'flex items-center gap-2 bg-primary/10 border border-primary/30 px-3 py-2 rounded text-xs text-foreground'
        },
          React.createElement(AttIcon, { className: 'h-4 w-4' }),
          React.createElement('span', null, att.filename),
          React.createElement('button', {
            onClick: function() { removePendingAttachment(att.id); },
            className: 'ml-1 text-destructive hover:text-destructive/80'
          },
            React.createElement(X, { className: 'h-3 w-3' })
          )
        );
      })
    ) : null;

  var previewModal = React.createElement(Dialog, {
    open: preview.open,
    onOpenChange: function(open) { setPreview(function(p) { return { ...p, open: open }; }); }
  },
    React.createElement(DialogContent, { className: 'max-w-4xl max-h-[90vh]' },
      React.createElement(DialogHeader, null,
        React.createElement(DialogTitle, { className: 'flex items-center gap-2' },
          preview.type === 'image' ? React.createElement(Image, { className: 'h-5 w-5' }) : React.createElement(FileText, { className: 'h-5 w-5' }),
          preview.filename
        )
      ),
      React.createElement('div', { className: 'mt-4 overflow-auto max-h-[70vh]' },
        preview.type === 'image' ?
          React.createElement('img', {
            src: preview.url,
            alt: preview.filename,
            className: 'max-w-full h-auto rounded'
          }) :
          React.createElement('iframe', {
            src: preview.url,
            className: 'w-full h-[600px] rounded bg-white'
          })
      )
    )
  );

  return React.createElement('div', { className: 'space-y-6', 'data-testid': 'portal-support-detail' },
    previewModal,
    React.createElement('div', { className: 'flex items-start justify-between' },
      React.createElement('div', null,
        React.createElement(Button, { asChild: true, variant: 'ghost', size: 'sm', className: 'text-muted-foreground hover:text-foreground mb-2' },
          React.createElement(Link, { to: '/portal/support' },
            React.createElement(ArrowLeft, { className: 'h-4 w-4 mr-2' }),
            'Zurück zur Übersicht'
          )
        ),
        React.createElement('div', { className: 'flex items-center gap-3' },
          React.createElement('h1', { className: 'text-2xl font-bold text-foreground' }, ticket.ticket_number),
          React.createElement(Badge, { className: statusConfig.className },
            React.createElement(StatusIcon, { className: 'h-3 w-3 mr-1' }),
            statusConfig.label
          )
        ),
        React.createElement('h2', { className: 'text-lg text-muted-foreground mt-1' }, ticket.subject)
      ),
      React.createElement(Button, { variant: 'outline', size: 'sm', onClick: fetchTicket },
        React.createElement(RefreshCw, { className: 'h-4 w-4 mr-2' }),
        'Aktualisieren'
      )
    ),
    !isClosedOrResolved ?
      React.createElement(Card, { className: 'glass-card' },
        React.createElement(CardHeader, { className: 'pb-3' },
          React.createElement(CardTitle, { className: 'text-lg flex items-center gap-2' },
            React.createElement(Send, { className: 'h-5 w-5 text-primary' }),
            'Antworten'
          )
        ),
        React.createElement(CardContent, null,
          React.createElement(Textarea, {
            value: replyText,
            onChange: function(e) { setReplyText(e.target.value); },
            placeholder: 'Deine Nachricht an den Support...',
            className: 'min-h-24 mb-4',
            'data-testid': 'portal-ticket-reply'
          }),
          pendingAttachmentElements,
          React.createElement('div', { className: 'flex items-center justify-between' },
            React.createElement('div', { className: 'flex items-center gap-2' },
              React.createElement('input', {
                type: 'file',
                ref: fileInputRef,
                onChange: handleFileSelect,
                accept: 'image/*,.pdf',
                multiple: true,
                className: 'hidden'
              }),
              React.createElement(Button, {
                variant: 'outline',
                size: 'sm',
                onClick: function() { fileInputRef.current && fileInputRef.current.click(); },
                disabled: uploading
              },
                uploading ? React.createElement(Loader2, { className: 'h-4 w-4 mr-2 animate-spin' }) : React.createElement(Paperclip, { className: 'h-4 w-4 mr-2' }),
                'Anhang'
              )
            ),
            React.createElement(Button, {
              onClick: handleSendReply,
              disabled: sending || !replyText.trim(),
              className: 'bg-primary hover:bg-primary/90',
              'data-testid': 'portal-ticket-send-btn'
            },
              sending ? React.createElement(Loader2, { className: 'h-4 w-4 mr-2 animate-spin' }) : React.createElement(Send, { className: 'h-4 w-4 mr-2' }),
              'Nachricht senden'
            )
          )
        )
      ) :
      React.createElement(Card, { className: 'bg-green-500/10 border-green-500/30' },
        React.createElement(CardContent, { className: 'py-4 text-center' },
          React.createElement(CheckCircle2, { className: 'h-8 w-8 text-green-600 dark:text-green-400 mx-auto mb-2' }),
          React.createElement('p', { className: 'text-green-700 dark:text-green-300' },
            'Dieses Ticket wurde ' + (ticket.status === 'resolved' ? 'gelöst' : 'geschlossen') + '. Bei neuen Fragen erstelle bitte eine neue Anfrage.'
          )
        )
      ),
    React.createElement(Card, { className: 'glass-card' },
      React.createElement(CardHeader, null,
        React.createElement(CardTitle, { className: 'text-lg' },
          'Konversation (' + messages.length + ' Nachricht' + (messages.length !== 1 ? 'en' : '') + ')'
        )
      ),
      React.createElement(CardContent, { className: 'space-y-4' },
        messages.length === 0 ?
          React.createElement('p', { className: 'text-muted-foreground text-center py-8' }, 'Keine Nachrichten') :
          messageElements
      )
    )
  );
};
